/**
 * QuickLogSheet — bottom sheet de 1-tap para registar refeição.
 *
 * Substitui o atrito de "CTA → /meals (página estática)" por 3 acções
 * directas: KetoScan agora, Receita IA, ou pick rápido da última refeição.
 *
 * Powered by GET /api/nutrition/macros-today (para mostrar o "que falta hoje"
 * e a janela de jejum se activa).
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, ChefHat, Clock, X, Zap, BookOpenCheck } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function QuickLogSheet({ open, onClose }) {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetch(`${API}/api/nutrition/macros-today`, { headers: authHeader() })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open]);

  const go = (path) => {
    onClose?.();
    navigate(path);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="quick-log-backdrop"
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            data-testid="quick-log-sheet"
            className="fixed bottom-0 inset-x-0 z-50 bg-gradient-to-b from-zinc-950 to-black border-t border-zinc-800 rounded-t-3xl p-5 max-w-2xl mx-auto"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 280, damping: 28 }}
          >
            <div className="flex justify-center mb-3">
              <div className="w-12 h-1.5 rounded-full bg-zinc-700" />
            </div>
            
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Quick Log</p>
                <h3 className="text-lg font-semibold text-zinc-100">
                  Registar refeição
                </h3>
              </div>
              <button
                onClick={onClose}
                data-testid="quick-log-close"
                className="text-zinc-400 hover:text-zinc-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {data && data.fasting?.active ? (
              <FastingBanner data={data} navigate={navigate} onClose={onClose} />
            ) : (
              <RemainingChips data={data} />
            )}

            <div className="grid grid-cols-3 gap-3 mt-5">
              <BigAction
                icon={<Camera className="w-6 h-6" />}
                label="KetoScan"
                desc="Foto + IA"
                onClick={() => go("/ketoscan")}
                testid="quick-action-ketoscan"
                primary
              />
              <BigAction
                icon={<ChefHat className="w-6 h-6" />}
                label="Receita IA"
                desc="Personalizada"
                onClick={() => go("/recipe-library")}
                testid="quick-action-recipe"
              />
              <BigAction
                icon={<BookOpenCheck className="w-6 h-6" />}
                label="Templates"
                desc="Guardadas"
                onClick={() => go("/meal-template")}
                testid="quick-action-templates"
              />
            </div>

            <Button
              variant="ghost"
              onClick={() => go("/meals")}
              className="w-full mt-3 text-zinc-400 hover:text-zinc-100"
              data-testid="quick-action-plan"
            >
              <Clock className="w-4 h-4 mr-2" />
              Ver plano semanal completo
            </Button>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function RemainingChips({ data }) {
  if (!data) return <div className="h-16 bg-zinc-900/40 rounded animate-pulse" />;
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="quick-log-remaining">
      <Chip label="Adiposa restante" value={`${data.remaining.fat_g.toFixed(0)}g`} tone="lime" />
      <Chip label="Reserva restante" value={`${data.remaining.protein_g.toFixed(0)}g`} tone="cyan" />
      <Chip
        label="Carbs livres"
        value={`${data.remaining.carb_g.toFixed(1)}g`}
        tone={data.carb_breached ? "red" : "amber"}
      />
    </div>
  );
}

function FastingBanner({ data, navigate, onClose }) {
  return (
    <div className="bg-violet-950/40 border border-violet-800/30 rounded-lg p-3" data-testid="quick-log-fasting-banner">
      <p className="text-violet-300 text-xs uppercase tracking-wider mb-1">Jejum activo</p>
      <p className="text-2xl font-mono text-violet-200">
        {data.fasting.remaining_hours?.toFixed(1) || "—"}h
      </p>
      <p className="text-xs text-zinc-400 mt-1">
        Hidrata. Quando quebrares: prioriza proteína + gordura.
      </p>
      <Button
        size="sm"
        variant="link"
        className="text-violet-300 px-0 mt-1"
        onClick={() => { onClose?.(); navigate("/fasting"); }}
        data-testid="quick-log-fasting-detail"
      >
        Ver timer →
      </Button>
    </div>
  );
}

function Chip({ label, value, tone }) {
  const toneClass = {
    lime: "bg-lime-950/50 text-lime-300 border-lime-800/40",
    cyan: "bg-cyan-950/50 text-cyan-300 border-cyan-800/40",
    amber: "bg-amber-950/50 text-amber-300 border-amber-800/40",
    red: "bg-red-950/50 text-red-300 border-red-800/40",
  }[tone] || "bg-zinc-900 text-zinc-200 border-zinc-800";
  return (
    <div className={`rounded p-2 text-center border ${toneClass}`}>
      <p className="text-[10px] uppercase opacity-70">{label}</p>
      <p className="font-mono text-sm font-semibold">{value}</p>
    </div>
  );
}

function BigAction({ icon, label, desc, onClick, testid, primary }) {
  const cls = primary
    ? "bg-lime-500 hover:bg-lime-400 text-black"
    : "bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border border-zinc-700";
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`${cls} rounded-xl p-3 flex flex-col items-center gap-1.5 transition-transform active:scale-95`}
    >
      {icon}
      <span className="font-semibold text-sm">{label}</span>
      <span className="text-[10px] opacity-70">{desc}</span>
    </button>
  );
}
